/**
 * The environment: one clock, one sun, one atmosphere, one set of numbers everything reads.
 *
 * ## Why this is a single object
 *
 * Before this milestone, "where is the sun" was a `Vector3` that four subsystems each had their
 * own copy of, and "how bright is it" did not exist at all. That works while the sun is a
 * constant. The moment there is a time-of-day slider it stops working, because the sky, the
 * directional light, the ambient fill, the water's reflected colour, the runway lights' dusk
 * fade, the star field, the cloud lighting and the exposure all have to change *together* and
 * stay consistent. Any one of them lagging by a frame, or deriving its own answer from a
 * slightly different model, shows up immediately as a scene lit from one direction and shaded
 * from another.
 *
 * So: this owns the clock and the atmosphere tables, and publishes plain values. It deliberately
 * touches almost no Three.js — a `Vector3` for direction and nothing else — which is what lets
 * `environment.test.ts` assert that dusk really is dimmer than noon without a GPU.
 *
 * ## The exposure model, and why it is not a histogram
 *
 * CLAUDE.md asks for auto-exposure that is "subtle, clamped". The usual implementation reads
 * back an average scene luminance and chases it. That has two problems here. It costs a GPU
 * readback and a frame of latency; and it *hunts* — point the nose at dark terrain and the sky
 * blows out, pan back up and everything dims, which on an aircraft that is constantly banking
 * is a permanently breathing image. It is the single most common way an otherwise good-looking
 * flight sim gives itself away.
 *
 * Since the atmosphere model already knows exactly how much light there is, exposure is computed
 * from *that* instead: a reference luminance built from the sky's own radiance and the sunlight
 * reaching the ground. It is stable by construction — it depends on the sun's elevation and the
 * weather, not on where the camera happens to point — it needs no readback, and it responds over
 * seconds rather than instantly, like an eye.
 *
 * `updateFrame(alpha)`: yes — {@link Environment.updateFrame} advances the clock, amortises the
 * sky table rebuild, and moves the exposure toward its target. Allocation-free.
 */

import { Vector3 } from 'three';
import { SkyViewTable } from './atmosphere/skyView.js';
import { copyRgb, rgb, type Rgb } from './atmosphere/model.js';
import {
  horizontalToRenderVector,
  refractionDeg,
  sunPosition,
  type HorizontalPosition,
} from '../world/celestial.js';
import { WEATHER, weatherFromPreset, type WeatherPresetName, type WeatherState } from '../world/weather.js';

const DEG = Math.PI / 180;

/**
 * The clock a session opens on, in local solar hours at the spawn airport.
 *
 * Ten rather than noon: a sun off the vertical is what gives terrain its relief, cloud a lit side
 * and a shadowed one, and the aeroplane a shadow with a direction in it. Noon flattens all three.
 */
export const DEFAULT_LOCAL_SOLAR_HOURS = 10;

/** Named times of day. Solved against the real sun, not hard-coded clock hours. */
export const TIME_PRESETS = ['dawn', 'golden', 'noon', 'dusk', 'night'] as const;
export type TimePresetName = (typeof TIME_PRESETS)[number];

/**
 * Target sun elevations for each preset, and which side of noon to look on.
 *
 * Solving for an elevation rather than fixing a clock time is what makes the presets mean the
 * same thing everywhere: "dusk" at Innsbruck in January and "dusk" at Innsbruck in July are four
 * hours apart on the clock and identical in the sky, which is the property that matters.
 */
const PRESET_TARGETS: Readonly<Record<TimePresetName, { elevationDeg: number; rising: boolean }>> =
  Object.freeze({
    dawn: { elevationDeg: 1.5, rising: true },
    golden: { elevationDeg: 6.0, rising: false },
    noon: { elevationDeg: 90, rising: false }, // unreachable: resolves to the day's maximum
    dusk: { elevationDeg: -0.5, rising: false },
    night: { elevationDeg: -20, rising: false },
  });

const scratchSun = /* #__PURE__ */ rgb();
const scratchAmbient = /* #__PURE__ */ rgb();
const scratchDirection = { x: 0, y: 0, z: 0 };

export interface EnvironmentOptions {
  latitudeDeg: number;
  longitudeDeg: number;
  /** Initial instant. Defaults to a summer late morning, which flatters a first screenshot. */
  utc?: Date;
  weather?: WeatherPresetName;
  /** Passed through to the sky table; tests use a small one. */
  skyView?: SkyViewTable;
}

export class Environment {
  readonly skyView: SkyViewTable;

  /** Direction *toward* the sun, render space, unit length. */
  readonly sunDirectionRender = new Vector3(0, 1, 0);
  /** Sunlight reaching the camera's altitude, per channel, relative to its value in space. */
  readonly sunRadiance: Rgb = rgb(1, 1, 1);
  /** Isotropic sky fill at the camera's altitude, per channel. */
  readonly ambient: Rgb = rgb(0, 0, 0);
  /**
   * Radiance of the sky just above the horizon, per channel.
   *
   * Read out of the baked table rather than recomputed. Two consumers need it and both would
   * otherwise invent their own: the water, whose grazing reflections are almost entirely
   * horizon sky, and the exposure model, for which it is the brightest part of the sky that is
   * usually in frame.
   */
  readonly horizonRadiance: Rgb = rgb(0, 0, 0);

  private utcValue: Date;
  private latitudeDeg: number;
  private longitudeDeg: number;
  private weatherValue: WeatherState;

  /** Real seconds per simulated second. 0 freezes the clock, which is the default. */
  private timeScaleValue = 0;
  private cameraAltitudeM = 0;
  private sunHorizontal: HorizontalPosition = { elevationDeg: 45, azimuthDeg: 180 };
  private exposureValue = 1;
  private exposureBiasValue = 1;
  private starIntensityValue = 0;
  /**
   * True once the instant has been chosen deliberately rather than defaulted.
   *
   * A `?date=`/`?time=` argument, the time slider, a preset, or "real now" all pin it. While it is
   * unpinned the clock re-anchors to {@link DEFAULT_LOCAL_SOLAR_HOURS} local solar at each new
   * spawn; once pinned, moving the aeroplane moves the sun, which is the physically honest
   * behaviour and the one somebody who set a time is asking for.
   */
  private pinnedInstant = false;

  constructor(options: EnvironmentOptions) {
    this.latitudeDeg = options.latitudeDeg;
    this.longitudeDeg = options.longitudeDeg;
    this.weatherValue = weatherFromPreset(options.weather ?? 'few');
    this.skyView = options.skyView ?? new SkyViewTable();

    /*
     * The default instant is a *local* one.
     *
     * A fixed UTC default means the opening view depends on which side of the planet you spawn
     * on: noon UTC is late morning over Europe and four in the morning over California, and the
     * first thing a new session showed at San Francisco was a black screen. Defaulting to
     * mid-morning local solar time gives the same good light wherever the aircraft starts, which
     * is what a default is for.
     *
     * 10:00 rather than noon because the sun being *off* the vertical is what gives terrain relief,
     * cloud a lit side and a shadowed one, and the aircraft a shadow with a direction — and the
     * default is the light every screenshot and every first session is taken in.
     *
     * It follows the aeroplane. {@link setObserver} re-solves it at the new longitude on every
     * spawn, so starting at Innsbruck gives the same morning light as starting at San Francisco
     * rather than whatever the San Francisco instant happens to be over the Alps — unless the time
     * has been set explicitly, which pins it. See {@link pinnedInstant}.
     */
    this.utcValue = options.utc ?? new Date(Date.UTC(2024, 5, 21));
    this.pinnedInstant = options.utc !== undefined;
    this.recomputeSun();
    if (options.utc === undefined) this.setLocalSolarHours(DEFAULT_LOCAL_SOLAR_HOURS, false);
    this.skyView.request(Math.sin(this.sunHorizontal.elevationDeg * DEG), 0);
    this.skyView.rebuildNow();
    this.refreshRadiance();
    // Start the exposure already adapted, so the first frame of a session — and the first frame
    // of a screenshot run — is correctly exposed rather than fading in from wherever.
    this.exposureValue = this.targetExposure();
  }

  // --- clock ---------------------------------------------------------------------------------

  get utc(): Date {
    return this.utcValue;
  }

  setUtc(utc: Date): void {
    this.pinnedInstant = true;
    this.utcValue = new Date(utc.getTime());
    this.recomputeSun();
  }

  /** Real seconds per wall-clock second. 60 makes a day pass in 24 minutes. */
  setTimeScale(scale: number): void {
    this.timeScaleValue = Math.max(0, scale);
  }

  get timeScale(): number {
    return this.timeScaleValue;
  }

  /**
   * Local *mean solar* time at the observer's longitude, in hours.
   *
   * Solar rather than civil, deliberately: a time-of-day slider should put the sun overhead at
   * 12 wherever you are, not wherever the nearest time-zone boundary happens to fall. Nobody
   * flying wants to discover that noon in western Spain is 13:40 on the slider.
   */
  get localSolarHours(): number {
    // Milliseconds included. Dropping them truncates to the nearest second, which is a fifth
    // of a second of error on a value the slider writes and then reads straight back — enough
    // to make the control visibly creep as it is dragged.
    const utcHours =
      this.utcValue.getUTCHours() +
      this.utcValue.getUTCMinutes() / 60 +
      this.utcValue.getUTCSeconds() / 3_600 +
      this.utcValue.getUTCMilliseconds() / 3_600_000;
    return wrap24(utcHours + this.longitudeDeg / 15);
  }

  setLocalSolarHours(hours: number, pin = true): void {
    if (pin) this.pinnedInstant = true;
    const utcHours = wrap24(hours - this.longitudeDeg / 15);
    const dayStart = Date.UTC(
      this.utcValue.getUTCFullYear(),
      this.utcValue.getUTCMonth(),
      this.utcValue.getUTCDate(),
    );
    this.utcValue = new Date(dayStart + utcHours * 3_600_000);
    this.recomputeSun();
  }

  /** Change the date, keeping the local time of day. */
  setDate(year: number, month: number, day: number): void {
    const hours = this.localSolarHours;
    this.utcValue = new Date(Date.UTC(year, month, day));
    this.setLocalSolarHours(hours);
  }

  /**
   * Jump to a named time of day by solving for the sun's elevation.
   *
   * A two-stage scan: five-minute steps across the whole day to bracket the crossing, then
   * bisection to land within a few seconds. Forty-odd `sunPosition` calls, which is nothing for
   * something that happens on a button press.
   *
   * Falls back to the day's extreme when the target elevation is unreachable — which is not an
   * edge case worth ignoring, because it is what happens every summer above the Arctic Circle,
   * where there is no dusk to solve for and the honest answer is "the lowest the sun gets".
   */
  setTimePreset(preset: TimePresetName): void {
    this.pinnedInstant = true;
    const target = PRESET_TARGETS[preset];
    const dayStart = Date.UTC(
      this.utcValue.getUTCFullYear(),
      this.utcValue.getUTCMonth(),
      this.utcValue.getUTCDate(),
    );

    const elevationAt = (minutesUtc: number): number =>
      sunPosition(new Date(dayStart + minutesUtc * 60_000), this.latitudeDeg, this.longitudeDeg)
        .elevationDeg;

    if (preset === 'noon') {
      let bestMinute = 0;
      let best = -91;
      for (let minute = 0; minute < 1_440; minute += 2) {
        const elevation = elevationAt(minute);
        if (elevation > best) {
          best = elevation;
          bestMinute = minute;
        }
      }
      this.utcValue = new Date(dayStart + bestMinute * 60_000);
      this.recomputeSun();
      return;
    }

    // Scan for a bracket where the elevation crosses the target in the requested direction.
    let previousMinute = 0;
    let previous = elevationAt(0);
    let foundLow: number | null = null;
    let foundHigh: number | null = null;
    let closestMinute = 0;
    let closestError = Number.POSITIVE_INFINITY;

    for (let minute = 5; minute <= 1_440; minute += 5) {
      const elevation = elevationAt(minute);
      const rising = elevation > previous;
      const error = Math.abs(elevation - target.elevationDeg);
      if (error < closestError) {
        closestError = error;
        closestMinute = minute;
      }
      const crosses =
        (previous - target.elevationDeg) * (elevation - target.elevationDeg) <= 0;
      if (crosses && rising === target.rising && foundLow === null) {
        foundLow = previousMinute;
        foundHigh = minute;
      }
      previous = elevation;
      previousMinute = minute;
    }

    let minute: number;
    if (foundLow === null || foundHigh === null) {
      // No crossing today: use whichever time came closest. Polar summer, polar night.
      minute = closestMinute;
    } else {
      let low = foundLow;
      let high = foundHigh;
      for (let i = 0; i < 20; i++) {
        const mid = (low + high) / 2;
        const crosses =
          (elevationAt(low) - target.elevationDeg) * (elevationAt(mid) - target.elevationDeg) <= 0;
        if (crosses) high = mid;
        else low = mid;
      }
      minute = (low + high) / 2;
    }

    this.utcValue = new Date(dayStart + minute * 60_000);
    this.recomputeSun();
  }

  // --- place ---------------------------------------------------------------------------------

  /** Move the observer. The world calls this when the floating origin is re-anchored. */
  setObserver(latitudeDeg: number, longitudeDeg: number): void {
    if (
      Math.abs(latitudeDeg - this.latitudeDeg) < 1e-4 &&
      Math.abs(longitudeDeg - this.longitudeDeg) < 1e-4
    ) {
      return;
    }
    // The local solar hour the clock is *currently* showing, read before the longitude moves —
    // re-solving it afterwards is what keeps "10:00 local" meaning 10:00 at the new airport.
    const hours = this.pinnedInstant ? null : this.localSolarHours;
    this.latitudeDeg = latitudeDeg;
    this.longitudeDeg = longitudeDeg;
    if (hours !== null) this.setLocalSolarHours(hours, false);
    this.recomputeSun();
  }

  get latitude(): number {
    return this.latitudeDeg;
  }

  get longitude(): number {
    return this.longitudeDeg;
  }

  // --- weather -------------------------------------------------------------------------------

  get weather(): WeatherState {
    return this.weatherValue;
  }

  setWeatherPreset(preset: WeatherPresetName): void {
    this.weatherValue = weatherFromPreset(preset);
  }

  /** Adjust one field of the current weather — the wind sliders and the cloud-base control. */
  patchWeather(patch: Partial<WeatherState>): void {
    this.weatherValue = { ...this.weatherValue, ...patch };
  }

  // --- what everything else reads ---------------------------------------------------------------

  /** The sun's *apparent* elevation, refraction included [deg]. */
  get sunElevationDeg(): number {
    return this.sunHorizontal.elevationDeg;
  }

  get sunAzimuthDeg(): number {
    return this.sunHorizontal.azimuthDeg;
  }

  /** `sin` of the sun's elevation — the `mu` the atmosphere tables are indexed by. */
  get sunMu(): number {
    return Math.sin(this.sunHorizontal.elevationDeg * DEG);
  }

  /** Multiplier applied to the whole HDR image before bloom and tone mapping. */
  get exposure(): number {
    return this.exposureValue * this.exposureBiasValue;
  }

  /** Manual exposure trim for the settings UI, 1 = neutral. */
  setExposureBias(bias: number): void {
    this.exposureBiasValue = Math.min(4, Math.max(0.25, bias));
  }

  /** How visible the stars are, 0…1. */
  get starIntensity(): number {
    return this.starIntensityValue;
  }

  /**
   * Advance the clock, keep the sky table current, and adapt the exposure.
   *
   * @param dtSeconds        real frame time.
   * @param cameraAltitudeM  true altitude above sea level, for the tables.
   * @returns whether the sky table was rebuilt this frame — the perf overlay reports it.
   */
  updateFrame(dtSeconds: number, cameraAltitudeM: number): boolean {
    this.cameraAltitudeM = Math.max(0, cameraAltitudeM);

    if (this.timeScaleValue > 0 && dtSeconds > 0) {
      this.utcValue = new Date(this.utcValue.getTime() + dtSeconds * this.timeScaleValue * 1000);
      this.recomputeSun();
    }

    this.skyView.request(this.sunMu, this.cameraAltitudeM);
    const rebuilt = this.skyView.updateFrame();

    this.refreshRadiance();

    /*
     * Adapt toward the target rather than snapping to it.
     *
     * Two different time constants, because eyes are not symmetric and neither are cameras:
     * adapting to *more* light is fast (a second or so — walking out of a hangar into sunlight)
     * and adapting to *less* is slow (many seconds — the reason you cannot see anything when you
     * walk back in). Modelling that asymmetry is what stops a dusk flight from strobing every
     * time the aircraft banks into and out of the sun.
     */
    const target = this.targetExposure();
    const brightening = target < this.exposureValue;
    const timeConstant = brightening ? 1.2 : 4.0;
    const blend = 1 - Math.exp(-Math.max(0, dtSeconds) / timeConstant);
    this.exposureValue += (target - this.exposureValue) * blend;

    return rebuilt;
  }

  /** Recompute the sun's position and the render-space direction toward it. */
  private recomputeSun(): void {
    const geometric = sunPosition(this.utcValue, this.latitudeDeg, this.longitudeDeg);
    // Refraction is an atmospheric effect, so it is applied here at the render boundary rather
    // than inside the ephemeris. It is worth half a degree at the horizon — slightly more than
    // the sun's own diameter, and therefore the difference between a sun sitting on the horizon
    // and one that has already gone.
    this.sunHorizontal = {
      elevationDeg: geometric.elevationDeg + refractionDeg(geometric.elevationDeg),
      azimuthDeg: geometric.azimuthDeg,
    };
    horizontalToRenderVector(this.sunHorizontal, scratchDirection);
    this.sunDirectionRender.set(scratchDirection.x, scratchDirection.y, scratchDirection.z);
  }

  /** Pull the sun's colour and the ambient fill out of the tables at the current altitude. */
  private refreshRadiance(): void {
    this.skyView.transmittance.sample(this.cameraAltitudeM, this.sunMu, scratchSun);
    copyRgb(this.sunRadiance, scratchSun);

    this.skyView.multipleScattering.sample(this.cameraAltitudeM, this.sunMu, scratchAmbient);
    copyRgb(this.ambient, scratchAmbient);

    /*
     * Stars appear through civil twilight and are fully out by the end of nautical twilight.
     * The real numbers: the brightest stars show around −3° of sun elevation, the sky is dark
     * enough for the Milky Way by about −12°. Squaring the ramp keeps them out of a sky that is
     * still visibly blue, which is where a linear ramp puts an obviously wrong scatter of dots.
     */
    const twilight = clamp01((-this.sunHorizontal.elevationDeg - 2) / 10);
    this.starIntensityValue = twilight * twilight;

    // A shade above the horizon rather than on it: exactly on it the ray grazes the ground and
    // the table's bottom rows are the dark side of the horizon, not the sky.
    this.skyView.sample(Math.PI / 2 - 0.12, Math.PI / 2, this.horizonRadiance);
  }

  /**
   * The exposure this scene wants.
   *
   * Built from two radiances the atmosphere model already knows, blended the way a scene is
   * actually composed: mostly ground, some sky. Both are *scene* quantities — neither depends on
   * where the camera is pointing, which is precisely why this does not hunt.
   */
  private targetExposure(): number {
    // Sky: the horizon band, which is the brightest part of the sky and the part most often in
    // frame. Refreshed by `refreshRadiance`, so it already includes multiple scattering.
    const skyLuminance = luminance(this.horizonRadiance);

    /*
     * Ground: a surface of average albedo, lit by the sun at its current elevation plus the
     * whole sky's ambient. The `/π` is the Lambertian BRDF and the `π` on the ambient term is
     * the integral of a uniform hemisphere — they are not decoration, they are what make the two
     * contributions comparable, and dropping either makes the ambient term dominate wrongly at
     * dusk.
     */
    const averageAlbedo = 0.25;
    const sunLuminance = luminance(this.sunRadiance) * Math.max(0, this.sunMu);
    const ambientLuminance = luminance(this.ambient);
    const groundLuminance = (averageAlbedo / Math.PI) * sunLuminance + averageAlbedo * ambientLuminance;

    // Overcast flattens the range: less direct sun, more sky. Weather has already changed both
    // terms above through the tables, so this only accounts for the cloud deck blocking the sun,
    // which the sky model does not know about.
    const cloudShading = 1 - this.weatherValue.coverage * 0.55;

    const reference = (0.68 * groundLuminance * cloudShading + 0.32 * skyLuminance);

    /*
     * Middle grey at 0.20, then clamped hard.
     *
     * The clamp is what makes night look like night. Without it the exposure would climb until
     * a moonlit landscape was as bright as noon, which is technically what an adapting eye does
     * and is completely wrong as an image: the whole *point* of night is that it is dark. The
     * ceiling is set so a clear night sits a few stops under a cloudy day.
     */
    const raw = 0.2 / Math.max(reference, 1e-6);
    return Math.min(48, Math.max(0.6, raw));
  }
}

function luminance(color: Rgb): number {
  return 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function wrap24(hours: number): number {
  const wrapped = hours % 24;
  return wrapped < 0 ? wrapped + 24 : wrapped;
}

/** Re-exported so callers do not need two imports to name a weather preset. */
export { WEATHER };
export type { WeatherPresetName, WeatherState };
